import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import CTASection from '../components/CTASection';
import TrustSignals from '../components/TrustSignals';
import { 
  CheckCircle2, 
  Home,
  Building,
  MapPin,
  Star,
  ArrowRight,
  Users,
  Waves
} from 'lucide-react';
import {
  combineSchemas,
  generateLocalBusinessSchema,
  generateServiceSchema,
  generateBreadcrumbSchema,
  generateWebPageSchema,
  generateFAQSchema
} from '../utils/schemaGenerator';

const MerrittIslandPage = () => {
  const services = [
    {
      title: "Recurring House Cleaning",
      description: "Regular cleaning for Merritt Island homes. Weekly, bi-weekly, or monthly service with the same reliable team every visit."
    },
    {
      title: "Deep Cleaning",
      description: "Comprehensive cleaning for waterfront and inland homes. Perfect for seasonal refreshes or preparing for guests and family visits."
    },
    {
      title: "Vacation Rental Cleaning",
      description: "Fast turnover cleaning for Merritt Island vacation rentals. Ready for guests with consistent quality and reliable scheduling."
    },
    {
      title: "Move-In / Move-Out Cleaning",
      description: "Inspection-ready cleaning for home transitions across the island. Trusted by renters, homeowners, and property managers."
    }
  ];

  const perfectFor = [
    "Waterfront homeowners along the Banana River and Indian River",
    "Families in established neighborhoods and new developments",
    "Vacation rental owners managing island properties",
    "Retirees and seasonal residents",
    "Professionals working at Kennedy Space Center",
    "Properties near Sykes Creek and the Pineda Causeway"
  ];

  const faqs = [
    {
      question: "Do you serve all of Merritt Island?",
      answer: "Yes. We serve all Merritt Island neighborhoods—from waterfront properties along the Banana River and Indian River to inland homes near Sykes Creek, the Pineda Causeway area, and throughout the island. We're familiar with the unique character of this community."
    },
    {
      question: "Can you help with vacation rental turnovers on Merritt Island?",
      answer: "Absolutely. Many Merritt Island property owners rent their homes seasonally or year-round. We handle fast turnovers with consistent quality, so your property is guest-ready on time, every time."
    },
    {
      question: "What makes Pierce Premier different from other cleaning companies serving Merritt Island?",
      answer: "We're locally owned by Bob and Heather—not a franchise. You'll have the same cleaners every visit, and Heather's 15+ years of experience means we deliver professional results consistently. We're accountable, responsive, and easy to reach."
    },
    {
      question: "Do you work with seasonal residents?",
      answer: "Yes. Many Merritt Island residents split their time between Florida and another location. We're flexible with scheduling and can accommodate seasonal service, one-time deep cleans when you arrive or leave, and everything in between."
    },
    {
      question: "Are you insured and bonded?",
      answer: "Yes. We're fully insured and bonded, and we take that responsibility seriously. Whether we're cleaning your family home or a high-value waterfront property, you can trust we're covered and professional."
    }
  ];

  const structuredData = combineSchemas(
    generateWebPageSchema({
      name: "House Cleaning Services in Merritt Island, FL | Pierce Premier",
      description: "Trusted house cleaning in Merritt Island, FL. Waterfront homes, vacation rentals, residential service. Same team every visit, 15+ years experience, fully insured. Call 321-408-1347.",
      url: "/areas/merritt-island"
    }),
    generateBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Service Areas", url: "/areas" },
      { name: "Merritt Island" }
    ]),
    generateLocalBusinessSchema(),
    generateServiceSchema({
      serviceName: "House Cleaning Services in Merritt Island",
      description: "Professional house cleaning, vacation rental turnover, and residential services in Merritt Island, FL."
    }),
    generateFAQSchema(faqs)
  );

  return (
    <>
      <SEO 
        title="House Cleaning Services in Merritt Island, FL | Pierce Premier"
        description="Trusted house cleaning in Merritt Island, FL. Waterfront homes, vacation rentals, residential. Same team, 15+ years experience, fully insured. Call 321-408-1347."
        canonical="/areas/merritt-island"
        structuredData={structuredData}
      />
      
      <div className="min-h-screen pt-20">
        <section className="bg-gradient-to-br from-cyan-50 via-blue-50 to-sky-50 py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-7xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-5 h-5 text-cyan-600" />
                  <Badge className="bg-cyan-600 text-white">Waterfront Community</Badge>
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                  House Cleaning Services in Merritt Island, FL
                </h1>
                <p className="text-base sm:text-lg text-gray-600 mb-6">
                  Merritt Island is where waterfront living meets everyday life—homes along the Banana River and Indian River, neighborhoods near Kennedy Space Center, and a mix of full-time residents, retirees, and seasonal visitors. Pierce Premier serves this unique island community with cleaning services built for the way people actually live here.
                </p>
                <p className="text-base sm:text-lg text-gray-600 mb-8">
                  Whether you're maintaining a family home, managing a vacation rental, or getting your property ready for the season, we bring the same commitment to quality and reliability every time.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Link 
                    to="/contact" 
                    className="inline-flex items-center justify-center bg-cyan-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-cyan-700 transition-colors"
                  >
                    Get Free Quote
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                  <a 
                    href="tel:+1-321-408-1347" 
                    className="inline-flex items-center justify-center border-2 border-cyan-600 text-cyan-600 px-8 py-4 rounded-lg font-semibold hover:bg-cyan-50 transition-colors"
                  >
                    Call: 321-408-1347
                  </a>
                </div>
                <TrustSignals variant="compact" />
              </div>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1559827260-dc66d52bef19?auto=format&fit=crop&w=800&q=80" 
                  alt="House cleaning services for Merritt Island waterfront and residential homes in Florida"
                  className="rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Why Merritt Island Residents and Vacation Rental Owners Choose Pierce Premier
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center mb-4">
                    <Waves className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Waterfront & Vacation Rental Expertise</h3>
                  <p className="text-gray-600 leading-relaxed">
                    We understand the unique needs of island properties—from managing salt air and sand to fast vacation rental turnovers. We've cleaned waterfront homes and rental properties throughout Merritt Island.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Same Team Every Time</h3>
                  <p className="text-gray-600 leading-relaxed">
                    No rotating crews. Whether you're a full-time resident with recurring service or a seasonal homeowner, you'll have the same cleaners who learn your property and your preferences.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center mb-4">
                    <Home className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Locally Owned & Accountable</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Bob and Heather personally oversee every job. We're not a franchise—we're a local business that stands behind our work and responds quickly when you need us.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center mb-4">
                    <Star className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">15+ Years of Experience</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Heather brings over 15 years of executive housekeeping experience to every Merritt Island property. We know what quality looks like, and we deliver it consistently.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-4xl">
            <Card className="border-2">
              <CardContent className="p-8 md:p-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                  Why We Love Serving Merritt Island
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Merritt Island has a distinct character—waterfront living, proximity to the space industry, and a community that includes everyone from Kennedy Space Center professionals to retirees who've found paradise. It's a place where people invest in their homes and take pride in their properties.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Whether you're here year-round or seasonally, maintaining a home or a rental property, we're glad to be part of keeping your island property clean and well-cared-for.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Services for Merritt Island Properties
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service, idx) => (
                <Card key={idx} className="border-2 hover:border-cyan-200 transition-colors">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{service.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Perfect For Merritt Island
              </h2>
              <p className="text-gray-600">Pierce Premier is trusted by:</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {perfectFor.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 bg-white rounded-lg border border-cyan-100">
                  <CheckCircle2 className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-900 font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Merritt Island Areas We Serve
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[
                "Banana River Waterfront",
                "Indian River Waterfront",
                "Sykes Creek Area",
                "Pineda Causeway Corridor",
                "North Merritt Island",
                "South Merritt Island"
              ].map((area, idx) => (
                <div key={idx} className="flex items-center gap-2 p-3 bg-cyan-50 rounded-lg border border-cyan-100">
                  <CheckCircle2 className="w-5 h-5 text-cyan-600 flex-shrink-0" />
                  <span className="text-gray-900 font-medium">{area}</span>
                </div>
              ))}
            </div>
            <div className="mt-8 text-center">
              <p className="text-gray-600 mb-2">
                <span className="font-semibold">ZIP Codes:</span> 32952, 32953
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                We Also Serve Nearby Communities
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { name: "Cocoa", link: "/areas/cocoa" },
                { name: "Cocoa Beach", link: "/areas/cocoa-beach" },
                { name: "Rockledge", link: "/areas/rockledge" },
                { name: "Melbourne", link: "/areas/melbourne" },
                { name: "Viera", link: "/areas/viera" },
                { name: "Satellite Beach", link: "/areas/satellite-beach" }
              ].map((city, idx) => (
                <Link key={idx} to={city.link} className="p-4 bg-cyan-50 rounded-lg border-2 border-cyan-200 hover:border-cyan-400 transition-colors text-center group">
                  <span className="text-cyan-700 font-semibold group-hover:text-cyan-900">{city.name}</span>
                  <ArrowRight className="w-4 h-4 inline-block ml-1 text-cyan-600" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Common Questions from Merritt Island Clients
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <Card key={idx} className="border-2">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <CTASection 
          title="Ready to Work with Pierce Premier?"
          description="Serving Merritt Island homeowners, vacation rental owners, and seasonal residents with reliable cleaning they can count on."
          primaryButtonText="Get Your Free Quote"
        />
      </div>
    </>
  );
};

export default MerrittIslandPage;
